import type { DesignSubmission } from "./design.js";

/** High-level phase of a room. */
export type GamePhase =
  | "lobby"
  | "writing"
  | "guessing"
  | "reveal"
  | "funniest"
  | "round_end"
  | "game_over";

/**
 * How rounds produce answers for this game session.
 * - text: classic typed answers every round
 * - design: shape illustrations every round
 * - caption: caption the same ridiculous photo every round
 * - mix: cycle text → design → caption → …
 */
export type ContentMode = "text" | "design" | "caption" | "mix";

/** What players submit this round. */
export type RoundKind = "text" | "design" | "caption";

export interface Player {
  id: string;
  name: string;
  /** True if this player created / currently hosts the room. */
  isHost: boolean;
  /** Cumulative score across rounds. */
  score: number;
  /** Connected right now. */
  connected: boolean;
  /** Soft color token for chips / avatars (index into palette). */
  colorIndex: number;
}

export interface Answer {
  id: string;
  kind: RoundKind;
  /** Text / caption answers; empty string for design rounds. */
  text: string;
  /** Shape data for design rounds. */
  design?: DesignSubmission;
  /** Author player id — never sent to clients until reveal. */
  authorId: string;
}

/** Public answer card (no author until reveal). */
export interface PublicAnswer {
  id: string;
  kind: RoundKind;
  text: string;
  design?: DesignSubmission;
  /** Present only in reveal / funniest / round_end. */
  authorId?: string;
  authorName?: string;
  /** How many players guessed this author correctly. */
  correctGuessCount?: number;
  /** Funniest votes received this round. */
  funniestVotes?: number;
}

/**
 * One player's assignment of authors to answer cards.
 * answerId → playerId
 */
export type GuessMap = Record<string, string>;

export interface RoundState {
  number: number;
  /** text / design / caption submission for this round. */
  kind: RoundKind;
  category: string;
  /**
   * Caption-round image id (from CAPTION_IMAGES catalog).
   * Clients resolve to a static asset URL.
   */
  imageId?: string;
  /** Server-only full answers; clients get publicAnswers. */
  answers: Answer[];
  /** Shuffled order of answer ids for display. */
  displayOrder: string[];
  /** playerId → guess map (answerId → guessedAuthorId) */
  guesses: Record<string, GuessMap>;
  /** playerId → answerId they voted funniest */
  funniestVotes: Record<string, string>;
  /** Players who finished the current sub-step */
  submitted: string[];
}

/** Full room snapshot (server truth). */
export interface RoomState {
  code: string;
  phase: GamePhase;
  players: Player[];
  hostId: string;
  /** Session content mode (host chooses in lobby). */
  contentMode: ContentMode;
  round: RoundState | null;
  /** Max rounds optional; 0 = unlimited until host stops. */
  maxRounds: number;
  createdAt: number;
  /** Points awarded last reveal (playerId → delta) for UI flair. */
  lastScoreDelta: Record<string, number>;
  /**
   * Epoch ms when the current timed phase ends (writing / guessing / funniest).
   * Undefined when no timer is running.
   */
  phaseEndsAt?: number;
}

/**
 * Client-safe view of the room.
 * Hides author identities until reveal-ish phases.
 */
export interface ClientRoomState {
  code: string;
  phase: GamePhase;
  players: Player[];
  hostId: string;
  contentMode: ContentMode;
  you: { id: string; name: string };
  maxRounds: number;
  createdAt: number;
  lastScoreDelta: Record<string, number>;
  /** Epoch ms when current timed phase ends; omit if no timer. */
  phaseEndsAt?: number;
  round: null | {
    number: number;
    kind: RoundKind;
    category: string;
    /** Caption-round image id when kind is caption. */
    imageId?: string;
    /** Whether you already submitted for this sub-phase. */
    youSubmitted: boolean;
    submittedCount: number;
    totalPlayers: number;
    answers: PublicAnswer[];
    /** Your current guesses (if any). */
    yourGuesses: GuessMap;
    /** Your funniest vote (if any). */
    yourFunniestVote: string | null;
    /** Funniest tallies (only after vote phase progresses / round_end). */
    funniestTallies?: { answerId: string; votes: number }[];
  };
}

/** Resolve text / design / caption for a round under the session content mode. */
export function resolveRoundKind(
  mode: ContentMode,
  roundNumber: number,
): RoundKind {
  if (mode === "text") return "text";
  if (mode === "design") return "design";
  if (mode === "caption") return "caption";
  // mix: text → design → caption → …
  const cycle: RoundKind[] = ["text", "design", "caption"];
  const idx = ((roundNumber - 1) % cycle.length + cycle.length) % cycle.length;
  return cycle[idx]!;
}

export function isContentMode(v: unknown): v is ContentMode {
  return (
    v === "text" || v === "design" || v === "caption" || v === "mix"
  );
}

export const POINTS = {
  /** Correctly identified an answer's author. */
  correctGuess: 100,
  /** Someone correctly guessed your answer (author bonus). */
  authorCorrectlyGuessed: 25,
  /** Received a funniest vote. */
  funniestVote: 50,
  /** Bonus if you received the most funniest votes (ties share). */
  funniestWinner: 100,
} as const;

/** Player chip palette — green family + metal neutrals on charcoal UI. */
export const PLAYER_COLORS = [
  "#39b54a", // brand green
  "#5fd36f", // soft green
  "#c8c8c8", // brushed metal
  "#7ec8e3", // cool steel
  "#e6b84d", // brass
  "#9ad4a3", // mint
  "#f0f0f0", // silver
  "#2d9a3c", // deep green
  "#a8d8b0", // pale green
  "#d4d4d4", // light metal
] as const;

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 12;
export const MAX_NAME_LENGTH = 20;
export const MAX_ANSWER_LENGTH = 140;

/** Max time for writing / guessing / funniest before force-submit. */
export const PHASE_TIME_LIMIT_MS = 3 * 60 * 1000;
/** Soft urgency (amber) when this much time remains. */
export const PHASE_WARN_MS = 45 * 1000;
/** Hard urgency (cop lights) when this much time remains. */
export const PHASE_CRITICAL_MS = 15 * 1000;
/** Placeholder when the writing timer forces an empty text/caption submit. */
export const TIMEOUT_ANSWER_TEXT = "(times up)";
